import { updateDuration } from "@/api/Activities";
import { Trans, useLingui } from "@lingui/react/macro";
import { FilledButton } from "@medoma/components/Button/Button";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import {
  ActivityDuration,
  type IActivityDurationFields,
} from "@/forms/AddActivityForm/ActivityDuration";
import {
  convertDurationToMinutes,
  convertMinutesToDurationFields,
} from "@/forms/AddActivityForm/activityDurationUtils";

export const EditDuration = ({
  currentDuration,
  onSuccess,
  activityId,
}: {
  currentDuration: number;
  onSuccess: () => void;
  activityId: string;
}) => {
  const { t } = useLingui();
  const methods = useForm<IActivityDurationFields>({
    defaultValues: convertMinutesToDurationFields(currentDuration),
  });

  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    reset,
    setError,
  } = methods;

  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (duration: number) => updateDuration(activityId, duration),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onSuccess();
    },
  });

  return (
    <FormProvider {...methods}>
      <Form
        onSubmit={handleSubmit((formData) => {
          const duration = convertDurationToMinutes(
            formData.durationOption,
            formData.customDuration,
            formData.customDurationUnit,
          );
          mutate(duration);
        })}
      >
        {isPending || isSuccess ? (
          <Loading message={t`Ändrar tidsåtgång`} />
        ) : (
          <>
            {errors.root?.server?.message && !isDirty ? (
              <ErrorMessage message={errors.root.server.message} />
            ) : null}
            <ActivityDuration />
            {isDirty ? (
              <FilledButton type="submit">
                <Trans>Spara ändringar</Trans>
              </FilledButton>
            ) : (
              <></>
            )}
          </>
        )}
      </Form>
    </FormProvider>
  );
};
